import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { POSITION_LABEL_MAX, measurementType, newId } from '@gk/contracts'
import { useClub, useEngineState } from '../../club/ClubProvider.tsx'
import type { NotebookReading, TargetRow } from '../../data/measure.ts'
import {
  GRID_MAX_COLS, GRID_MAX_ROWS, SAND_TYPES, VERY_LOW_SHARE, WALK_UNITS, addLabelProblem, bunkerGrid, cellsToWork, checkWalk, formLayout, isSandType, lastValueText, quickGrid, startDraft, walkDays, walkPayloads, withLabels,
  draftLabels, type AddLabelProblem, type BunkerGrid, type CellVerdict, type GridCell, type GridSide, type SandType, type WalkDraft,
} from '../../data/bunkerUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate } from '../../lib/time.ts'
import { Banner, DisabledHint, Field, Section, errorText } from '../../ui/components.tsx'
import { useAction, useWriteAccess } from '../../ui/hooks.ts'
import { lang2, unitSymbol, type AreaOption } from './shared.tsx'

/** M16-D: "Relevé de sable" on a bunker's page of the notebook (docs/M16.md D). The walk is readings (measurement.record@2, one per cell of the club's grid,
 *  the floor and the face): the grid, the verdict of each cell against the club's target of the month and the previous walk beside are a view over them
 *  (data/bunkerUi.ts); the form types a new walk, offline like every reading; "Créer le travail correctif" opens the planner's new-job form with the bunker and
 *  a note naming the walk (an ordinary job.create@1: nothing links the two). */
const french = (iso: string) => iso.split('-').reverse().join('/')
type T = (key: string, o?: Record<string, unknown>) => string
const sideWord = (t: T, type: SandType) => (type === 'sand_depth_floor' ? t('bunker.side.floor') : t('bunker.side.face'))
const sideLower = (t: T, type: SandType) => (type === 'sand_depth_floor' ? t('bunker.sideLower.floor') : t('bunker.sideLower.face'))
const verdictWord = (t: T, v: CellVerdict) => (v === 'in' ? t('bunker.verdict.in') : v === 'low' ? t('bunker.verdict.low') : v === 'veryLow' ? t('bunker.verdict.veryLow') : t('bunker.verdict.high'))
const labelProblemWord = (t: T, p: AddLabelProblem) => (p === 'empty' ? t('bunker.form.addProblem.empty') : p === 'exists' ? t('bunker.form.addProblem.exists') : t('bunker.form.addProblem.tooLong', { max: POSITION_LABEL_MAX }))
// the verdict's tone: theme tokens with their night colours (in band fairway, low or too high sand, very low flag; no target slate)
const TONE: Record<CellVerdict | 'none', string> = {
  in: 'bg-fairway/20 text-brand border-fairway/40 dark:bg-fairway/25 dark:text-emerald-100 dark:border-fairway/40',       // the fairway token as text was too faint on the night card
  low: 'bg-sand text-sand-ink border-sand-ink/30 dark:bg-sand/20 dark:text-sand dark:border-sand/30',
  high: 'bg-sand text-sand-ink border-sand-ink/30 dark:bg-sand/20 dark:text-sand dark:border-sand/30',
  veryLow: 'bg-red-100 text-flag border-flag/40 dark:bg-red-900/40 dark:text-red-200 dark:border-red-400/50',
  none: 'bg-slate-100 text-slate-900 border-slate-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600',
}

/** The page passes the notebook it has already loaded (CLAUDE.md M4: a component of its own with its own live query would read nothing on its first render, and
 *  the form, seeded once, would open with no cell). */
export function BunkerWalkSection({ area, readings: all, targets }: { area: AreaOption; readings: readonly NotebookReading[]; targets: readonly TargetRow[] }) {
  const { t, i18n } = useTranslation()
  const { site, kiosk } = useClub()
  const record = useWriteAccess('measurement.record')
  const plan = useWriteAccess('job.plan')
  const today = localDate(new Date(), site.timezone)
  const readings = useMemo(() => all.filter((r) => r.area_id === area.id && isSandType(r.measurement_type)), [all, area.id])
  const days = useMemo(() => walkDays(readings), [readings])
  const [shown, setShown] = useState('')
  const walkDate = shown && days.includes(shown) ? shown : days[0] ?? null
  const previous = walkDate ? days[days.indexOf(walkDate) + 1] ?? null : null
  const grid = useMemo(() => bunkerGrid(readings, targets, walkDate, previous, area.typeCode), [readings, targets, walkDate, previous, area.typeCode])
  // the form always shows the LATEST walk beside each cell (the one the new walk will be compared with)
  const latest = useMemo(() => (walkDate === (days[0] ?? null) ? grid : bunkerGrid(readings, targets, days[0] ?? null, days[1] ?? null, area.typeCode)), [grid, walkDate, days, readings, targets, area.typeCode])
  const methods = useMemo(() => [...new Set(all.filter((r) => isSandType(r.measurement_type)).map((r) => r.method).filter(Boolean) as string[])], [all])
  const work = cellsToWork(grid)
  const note = walkDate ? (work.length ? t('bunker.jobNote', { date: french(walkDate), cells: work.map((w) => `${sideLower(t, w.type)} ${w.labels.slice(0, 20).join(', ')}`).join(' ; ') }) : t('bunker.jobNoteAll', { date: french(walkDate) })) : ''

  return (
    <Section title={t('bunker.title')} help={t('bunker.help')} testId="bunker-walk">
      {days.length === 0 ? <p className="meta" data-testid="bunker-none">{t('bunker.none')}</p> : (
        <div className="card flex flex-col gap-4" data-testid="bunker-grid" data-walk={walkDate ?? ''} data-previous={previous ?? ''}>
          <div className="flex flex-col gap-1">
            <p className="text-lg font-semibold" data-testid="bunker-walk-date">{t('bunker.walkOf', { date: french(walkDate!) })}</p>
            <p className="meta" data-testid="bunker-previous">{previous ? t('bunker.comparedTo', { date: french(previous) }) : t('bunker.first')}</p>
          </div>
          {days.length > 1 && (
            <Field label={t('bunker.pickWalk')} htmlFor="bunker-pick">
              <select id="bunker-pick" className="input" value={walkDate ?? ''} onChange={(e) => setShown(e.target.value)} data-testid="bunker-pick">
                {days.map((d) => <option key={d} value={d}>{french(d)}</option>)}
              </select>
            </Field>
          )}
          {grid.sides.map((s) => <SideGrid key={s.type} side={s} title={sideWord(t, s.type)} />)}
          <p className="meta" data-testid="bunker-rule">{t('bunker.rule', { pct: Math.round(VERY_LOW_SHARE * 100) })}</p>
          {plan.permitted && walkDate && (
            <div className="flex flex-col gap-1">
              {/* the planner's new-job form, with the bunker and the walk named in the note: an ordinary job.create@1 (rule 1: nothing links the two) */}
              <Link to="/plan/new" search={{ d: today, area: area.id, note }} className="btn" data-testid="bunker-corrective" data-note={note}>{t('bunker.corrective')}</Link>
              <p className="meta">{t('bunker.correctiveHelp')}</p>
            </div>
          )}
        </div>
      )}
      {record.permitted && <WalkForm key={area.id} areaId={area.id} latest={latest} days={days} methods={methods} today={today} kiosk={!!kiosk} lang={i18n.language} />}
    </Section>
  )
}

function SideGrid({ side, title }: { side: GridSide; title: string }) {
  const { t, i18n } = useTranslation()
  const mm = unitSymbol('mm', i18n.language)
  const band = side.target ? [side.target.low !== null ? fmtNum(side.target.low, i18n.language) : '…', side.target.high !== null ? fmtNum(side.target.high, i18n.language) : '…'].join(' – ') : null
  const byPlace = new Map(side.cells.map((c) => [`${c.row}|${c.col}`, c]))
  const empty = side.cells.length === 0 && side.others.length === 0 && !side.unlabelled
  const toWork = (['low', 'veryLow', 'high'] as const).map((v) => ({ v, labels: [...side.cells, ...side.others].filter((c) => c.verdict === v).map((c) => c.label) })).filter((x) => x.labels.length)
  return (
    <div className="flex flex-col gap-2" data-testid="bunker-side" data-type={side.type}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-lg font-bold">{title}</h3>
        <span className="meta" data-testid="bunker-target">{band ? t('bunker.target', { band, unit: mm }) : t('bunker.noTarget')}</span>
      </div>
      {empty ? <p className="meta">{t('bunker.sideNone')}</p> : (
        <>
          {side.cells.length > 0 && (
            <div className="overflow-x-auto">
              <div className="grid gap-1.5" style={{ gridTemplateColumns: `1.75rem repeat(${side.cols.length}, minmax(3.75rem, 1fr))` }} role="grid" aria-label={title}>
                <span />
                {side.cols.map((c) => <span key={c} className="text-center text-[13px] font-semibold" role="columnheader">{c}</span>)}
                {side.rows.map((r) => (
                  <div key={r} className="contents" role="row">
                    <span className="self-center text-[13px] font-semibold" role="rowheader">{r}</span>
                    {side.cols.map((c) => { const cell = byPlace.get(`${r}|${c}`); return cell ? <Cell key={c} cell={cell} /> : <span key={c} className="min-h-14 rounded-lg border border-dashed border-slate-300 dark:border-slate-700" aria-hidden /> })}
                  </div>
                ))}
              </div>
            </div>
          )}
          {side.others.length > 0 && <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">{side.others.map((c) => <Cell key={c.label} cell={c} named />)}</div>}
          {side.unlabelled && side.unlabelled.value !== null && (
            <p className="meta" data-testid="bunker-unlabelled">{t('bunker.unlabelled', { count: side.unlabelled.n, value: fmtNum(side.unlabelled.value, i18n.language, 0), unit: mm })}</p>
          )}
          <p className="text-sm" data-testid="bunker-to-work">
            {!side.target ? t('bunker.notJudged') : toWork.length === 0 ? t('bunker.allIn') : toWork.map((x) => `${verdictWord(t, x.v)} : ${x.labels.join(', ')}`).join(' · ')}
          </p>
        </>
      )}
    </div>
  )
}

function Cell({ cell, named = false }: { cell: GridCell; named?: boolean }) {
  const { t, i18n } = useTranslation()
  const mm = unitSymbol('mm', i18n.language)
  const v = cell.value === null ? null : fmtNum(cell.value, i18n.language, 0)
  const before = cell.previous === null ? null : fmtNum(cell.previous, i18n.language, 0)
  const delta = cell.delta === null ? null : `${cell.delta > 0 ? '+' : cell.delta < 0 ? '−' : '±'}${fmtNum(Math.abs(cell.delta), i18n.language, 0)}`
  const aria = [cell.label, v !== null ? `${v} ${mm}` : t('bunker.cellNotRead'), cell.verdict ? verdictWord(t, cell.verdict) : null, before !== null ? t('bunker.before', { value: `${before} ${mm}` }) : null].filter(Boolean).join(', ')
  const tone = cell.value === null ? 'border-dashed border-slate-300 dark:border-slate-600' : TONE[cell.verdict ?? 'none']
  return (
    <div className={`flex min-h-14 flex-col justify-center rounded-lg border px-1.5 py-1 ${tone}`} role="gridcell" aria-label={aria}
         data-testid="bunker-cell" data-label={cell.label} data-value={cell.value ?? ''} data-verdict={cell.verdict ?? ''} data-previous={cell.previous ?? ''} data-pending={cell.pending ? '1' : ''}>
      {named && <span className="truncate text-[13px] font-semibold">{cell.label}</span>}
      <span className="text-base font-bold leading-tight">{v ?? '—'}</span>
      {(before !== null || delta !== null) && <span className="text-[13px] leading-tight">{delta ?? t('bunker.beforeShort', { value: before })}</span>}
      {cell.pending && <span className="text-[13px] leading-tight">{t('bunker.pending')}</span>}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- typing a walk (field facts, offline)
function WalkForm({ areaId, latest, days, methods, today, kiosk, lang }: { areaId: string; latest: BunkerGrid; days: string[]; methods: string[]; today: string; kiosk: boolean; lang: string }) {
  const { t } = useTranslation()
  const { send, site } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('measurement.record')
  const { busy, error, run } = useAction()
  // seeded once from the walk the parent has already loaded (CLAUDE.md M4: never seed state from a live query's first render)
  const [d, setD] = useState<WalkDraft>(() => startDraft(latest, today))
  const [done, setDone] = useState<string | null>(null)
  const ids = useRef<string[]>([])
  const check = checkWalk(d, today)
  // a walk that arrives later (a sync, another phone) adds its cells to the form; nothing typed is touched and no cell is taken away
  const labelsKey = SAND_TYPES.map((ty) => draftLabels(latest, ty).join('|')).join('/')
  useEffect(() => { setD((x) => ({ ...x, cells: Object.fromEntries(SAND_TYPES.map((ty) => [ty, withLabels(x.cells[ty], draftLabels(latest, ty))])) as WalkDraft['cells'] })) }, [labelsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  // the cell's most recent value: the last walk's, or the walk before it when the last one skipped the cell
  const lastOf = (type: SandType, label: string) => { const s = latest.sides.find((x) => x.type === type); const c = s && [...s.cells, ...s.others].find((x) => x.label === label); return c ? c.value ?? c.previous : null }
  const setCells = (type: SandType, f: (cells: WalkDraft['cells'][SandType]) => WalkDraft['cells'][SandType]) => { setDone(null); setD((x) => ({ ...x, cells: { ...x.cells, [type]: f(x.cells[type]) } })) }
  const mm = unitSymbol('mm', lang)

  function submit(e: FormEvent) {
    e.preventDefault(); setDone(null)
    void run(async () => {
      // the ids are kept until the walk is sent, so a second tap after an error sends the same commands again (idempotent), never a second walk
      let k = 0
      const payloads = walkPayloads(d, { areaId, today, tz: site.timezone, newId: () => (ids.current[k++] ??= newId()) })
      for (const p of payloads) await send('measurement.record@2', p)
      ids.current = []
      setDone(engine.online === false || !navigator.onLine ? t('bunker.form.savedOffline', { count: payloads.length }) : t('bunker.form.saved', { count: payloads.length }))
      setD((x) => ({ ...x, cells: Object.fromEntries(SAND_TYPES.map((ty) => [ty, x.cells[ty].map((c) => ({ ...c, value: '' }))])) as WalkDraft['cells'] }))
    })
  }
  return (
    <form className="card flex flex-col gap-4" onSubmit={submit} data-testid="bunker-form">
      <h3 className="text-lg font-bold">{t('bunker.form.title')}</h3>
      {kiosk && <p className="meta" data-testid="bunker-kiosk">{t('bunker.form.kiosk')}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('bunker.form.day')} hint={t('bunker.form.dayHint')} htmlFor="bunker-day">
          <input id="bunker-day" type="date" className="input" max={today} value={d.day} onChange={(e) => { setDone(null); setD((x) => ({ ...x, day: e.target.value })) }} data-testid="bunker-day" />
        </Field>
        <Field label={t('bunker.form.unit')} htmlFor="bunker-unit">
          <select id="bunker-unit" className="input" value={d.unit} onChange={(e) => setD((x) => ({ ...x, unit: e.target.value }))} data-testid="bunker-unit">
            {WALK_UNITS.map((u) => <option key={u} value={u}>{unitSymbol(u, lang)}</option>)}
          </select>
        </Field>
      </div>
      {days.includes(d.day) && <p className="meta" data-testid="bunker-same-day">{t('bunker.form.sameDay')}</p>}
      <p className="meta">{t('bunker.form.cellsHelp')}</p>
      {SAND_TYPES.map((type) => (
        <SideForm key={type} type={type} cells={d.cells[type]} unit={d.unit} lastOf={(l) => lastOf(type, l)} mm={mm} lang={lang} setCells={(f) => setCells(type, f)} />
      ))}
      <Field label={t('bunker.form.method')} htmlFor="bunker-method">
        <input id="bunker-method" className="input" list="bunker-methods" maxLength={80} value={d.method} onChange={(e) => setD((x) => ({ ...x, method: e.target.value }))} data-testid="bunker-method" />
      </Field>
      <datalist id="bunker-methods">{methods.map((m) => <option key={m} value={m} />)}</datalist>
      {check.problems.length > 0 && (
        <Banner tone="warn" testId="bunker-check">
          {check.problems.map((p, i) => <span key={i} className="block">{p.type ? `${sideWord(t, p.type)} ${p.label} : ` : ''}{errorText(t, p.code)}</span>)}
        </Banner>
      )}
      {error && <Banner tone="error"><span data-testid="bunker-error">{error}</span></Banner>}
      {done && <Banner tone="ok"><span data-testid="bunker-done">{done}</span></Banner>}
      <DisabledHint show={check.filled === 0} testId="bunker-save-hint">{t('bunker.form.saveHint')}</DisabledHint>
      <button className="btn btn-primary" disabled={busy || access.readOnlyMode || !check.ok} data-testid="bunker-save">{t('bunker.form.save', { count: check.filled })}</button>
    </form>
  )
}

function SideForm({ type, cells, unit, lastOf, mm, lang, setCells }: {
  type: SandType; cells: WalkDraft['cells'][SandType]; unit: string; lastOf: (label: string) => number | null; mm: string; lang: string
  setCells: (f: (cells: WalkDraft['cells'][SandType]) => WalkDraft['cells'][SandType]) => void
}) {
  const { t } = useTranslation()
  const [label, setLabel] = useState('')
  const [rows, setRows] = useState(2)
  const [cols, setCols] = useState(3)
  const problem = label.trim() === '' ? null : addLabelProblem(label, cells)
  const def = measurementType(type)
  const lay = formLayout(cells.map((c) => c.label))
  const byLabel = new Map(cells.map((c) => [c.label, c]))
  const input = (c: WalkDraft['cells'][SandType][number]) => {
    const last = lastOf(c.label)
    const id = `bunker-${type}-${c.label.replace(/[^A-Za-z0-9_-]/g, '_')}`
    return (
      <div key={c.label} className="flex min-w-0 flex-col gap-1" data-testid="bunker-input" data-label={c.label}>
        <label htmlFor={id} className="text-sm font-semibold">{c.label}</label>
        <input id={id} className="input text-xl" inputMode="decimal" autoComplete="off" value={c.value} aria-describedby={last !== null ? `${id}-last` : undefined}
               onChange={(e) => { const v = e.target.value; setCells((cs) => cs.map((x) => (x.label === c.label ? { ...x, value: v } : x))) }} data-testid="bunker-value" />
        {/* the last value in the unit being typed (display only), the stored millimetres beside */}
        <span id={`${id}-last`} className="meta">{last !== null ? t('bunker.form.last', { value: lastValueText(last, unit, (u) => unitSymbol(u, lang), (n, d) => fmtNum(n, lang, d)) }) : t('bunker.form.noLast')}</span>
      </div>
    )
  }
  const title = sideWord(t, type)
  return (
    <fieldset className="flex flex-col gap-2" data-testid="bunker-form-side" data-type={type}>
      <legend className="text-base font-bold">{title}{def ? <span className="meta"> · {def.label[lang2(lang)]}</span> : null}</legend>
      {cells.length === 0 ? (
        <div className="flex flex-col gap-2" data-testid="bunker-quick">
          <p className="meta">{t('bunker.form.noCells')}</p>
          <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
            <Field label={t('bunker.form.rows')} htmlFor={`bq-rows-${type}`}>
              <select id={`bq-rows-${type}`} className="input" value={rows} onChange={(e) => setRows(Number(e.target.value))} data-testid="bunker-quick-rows">
                {Array.from({ length: GRID_MAX_ROWS }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <Field label={t('bunker.form.cols')} htmlFor={`bq-cols-${type}`}>
              <select id={`bq-cols-${type}`} className="input" value={cols} onChange={(e) => setCols(Number(e.target.value))} data-testid="bunker-quick-cols">
                {Array.from({ length: GRID_MAX_COLS }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <button type="button" className="btn" onClick={() => setCells((c) => withLabels(c, quickGrid(rows, cols)))} data-testid="bunker-quick-go">{t('bunker.form.quickGo')}</button>
          </div>
        </div>
      ) : (
        <>
          {/* release b review (finding 12): on the grid's own rows and columns, as the view draws it; the club's other words below */}
          {lay.rows.length > 0 && (
            <div className="overflow-x-auto">
              <div className="grid gap-2" style={{ gridTemplateColumns: `1.75rem repeat(${lay.cols.length}, minmax(5rem, 1fr))` }}>
                <span />
                {lay.cols.map((c) => <span key={c} className="text-center text-[13px] font-semibold">{c}</span>)}
                {lay.rows.map((r) => (
                  <div key={r} className="contents" data-testid="bunker-form-row" data-row={r}>
                    <span className="self-center text-[13px] font-semibold">{r}</span>
                    {lay.cols.map((c) => { const l = lay.at.get(`${r}|${c}`); const cell = l ? byLabel.get(l) : undefined; return cell ? input(cell) : <span key={c} aria-hidden /> })}
                  </div>
                ))}
              </div>
            </div>
          )}
          {lay.others.length > 0 && <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{lay.others.map((l) => input(byLabel.get(l)!))}</div>}
        </>
      )}
      <div className="grid grid-cols-[1fr_auto] items-end gap-2">
        <Field label={t('bunker.form.addLabel')} htmlFor={`bunker-add-${type}`} error={problem ? labelProblemWord(t, problem) : null}>
          <input id={`bunker-add-${type}`} className="input" maxLength={POSITION_LABEL_MAX + 4} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="bunker-add-label"
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (label.trim() && !problem) { setCells((cs) => withLabels(cs, [label])); setLabel('') } } }} />
        </Field>
        <button type="button" className="btn" disabled={label.trim() === '' || !!problem} onClick={() => { setCells((cs) => withLabels(cs, [label])); setLabel('') }} data-testid="bunker-add-go">{t('bunker.form.addGo')}</button>
      </div>
      {unit !== 'mm' && <p className="meta">{t('bunker.form.unitNote', { unit: unitSymbol(unit, lang), mm })}</p>}
    </fieldset>
  )
}
